// MEO landmark ads (Phase 1, Step 4-4, 2026-05-01):
// 占い文言プール管理画面。
//
// Spec: design/meo-landmark-ads-spec.md §UX/UI §管理者 §占い文言プール管理画面
//
// MVP scope (本 PR):
// - filter: fortune / motif / language / status
// - list table (本文 / 階層 / モチーフ / 言語 / 採否 / 使用回数 / 生成元 / 生成日)
// - 採否マーク変更 (active / rejected / favorite)
// - 「追加生成」 modal (Claude API 呼び出し)
// Out of scope (将来 PR): 5×5×5 グリッド表示、本文の inline 手動編集

import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { toastify } from '@/utils/toastify';
import { FORTUNE_LABEL, MOTIF_LABEL } from '@/utils/omikujiLabels';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const FORTUNE_OPTIONS = [
    { value: 'all', label: 'すべて' },
    { value: '1', label: '大吉' },
    { value: '2', label: '中吉' },
    { value: '3', label: '小吉' },
    { value: '4', label: '末吉' },
    { value: '5', label: '凶' }
];

const MOTIF_OPTIONS = [
    { value: 'all', label: 'すべて' },
    { value: 'whaleshark', label: 'ジンベエザメ' },
    { value: 'turtle', label: 'アオウミガメ' },
    { value: 'yambaru_kuina', label: 'ヤンバルクイナ' },
    { value: 'akashobin', label: 'アカショウビン' },
    { value: 'mangrove', label: '月夜のマングローブ' }
];

const LANGUAGE_OPTIONS = [
    { value: 'all', label: 'すべて' },
    { value: 'ja', label: '日本語' },
    { value: 'en', label: '英語' },
    { value: 'zh-CN', label: '中国語(簡体)' },
    { value: 'zh-TW', label: '中国語(繁体)' },
    { value: 'ko', label: '韓国語' }
];

const STATUS_OPTIONS = [
    { value: 'all', label: 'すべて' },
    { value: '1', label: '採用 (active)' },
    { value: '2', label: '却下 (rejected)' },
    { value: '3', label: 'お気に入り (favorite)' }
];

const STATUS_VALUE_TO_LABEL: Record<number, string> = {
    1: '採用',
    2: '却下',
    3: 'お気に入り'
};

const STATUS_VALUE_TO_COLOR: Record<number, string> = {
    1: 'bg-green-100 text-green-800',
    2: 'bg-red-100 text-red-800',
    3: 'bg-yellow-100 text-yellow-800'
};

const LANG_LABEL: Record<string, string> = {
    'ja': '日本語',
    'en': '英語',
    'zh-CN': '中国語(簡体)',
    'zh-TW': '中国語(繁体)',
    'ko': '韓国語'
};

// 「追加生成」 form 用 schema
const generateSchema = yup.object({
    fortune: yup.number().required('階層を選択してください').min(1).max(5),
    motif: yup
        .string()
        .required('モチーフを選択してください')
        .oneOf(MOTIF_OPTIONS.slice(1).map(o => o.value), '対応外のモチーフです'),
    language: yup
        .string()
        .required('言語を選択してください')
        .oneOf(LANGUAGE_OPTIONS.slice(1).map(o => o.value), '対応外の言語です'),
    count: yup
        .number()
        .typeError('数値を入力してください')
        .required('件数を入力してください')
        .integer('整数で入力してください')
        .min(1, '1 以上')
        .max(50, '50 以下 (Claude API レート制限考慮)')
});

export default observer(function OmikujiTextsManagementPage() {
    const { t } = useTranslation();
    const {
        omikujiTextStore: { items, total, list: fetchList, update: updateText, generate: generateTexts }
    } = useStore();

    const [fFortune, setFFortune] = useState<string>('all');
    const [fMotif, setFMotif] = useState<string>('all');
    const [fLanguage, setFLanguage] = useState<string>('all');
    const [fStatus, setFStatus] = useState<string>('all');
    const [generateOpen, setGenerateOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const buildFilters = () => {
        const f: any = { page: 1, limit: 100 };
        // 'all' は filter なし扱い、それ以外を api に渡す
        if (fFortune && fFortune !== 'all') f.fortune = fFortune;
        if (fMotif && fMotif !== 'all') f.motif = fMotif;
        if (fLanguage && fLanguage !== 'all') f.language = fLanguage;
        if (fStatus && fStatus !== 'all') f.status = fStatus;
        return f;
    };

    const reload = async () => {
        setLoading(true);
        try {
            await flowResult(fetchList(buildFilters()));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusChange = async (id: number, newStatus: number) => {
        const res = await flowResult(updateText({ id, data: { status: newStatus } }));
        if (res?.ok) {
            toastify('success', '採否を更新しました');
            reload();
        } else {
            toastify('error', res?.message || '更新に失敗しました');
        }
    };

    return (
        <div className='space-y-3'>
            <div className='layout-card'>
                <div className='flex flex-wrap items-end gap-3'>
                    <h2 className='text-[16px] font-bold mr-3'>
                        {t('words_title.omikuji_texts_management') || '占い文言プール'}
                    </h2>
                    <FilterSelect label='階層' value={fFortune} onChange={setFFortune} options={FORTUNE_OPTIONS} />
                    <FilterSelect label='モチーフ' value={fMotif} onChange={setFMotif} options={MOTIF_OPTIONS} />
                    <FilterSelect label='言語' value={fLanguage} onChange={setFLanguage} options={LANGUAGE_OPTIONS} />
                    <FilterSelect label='採否' value={fStatus} onChange={setFStatus} options={STATUS_OPTIONS} />
                    <Button variant='outline' onClick={reload} disabled={loading}>
                        {loading ? '読み込み中…' : '検索'}
                    </Button>
                    <Button variant='active' onClick={() => setGenerateOpen(true)} className='ml-auto'>
                        追加生成
                    </Button>
                    <span className='text-sm text-gray-500'>
                        {`件数: ${items.length} / ${total}`}
                    </span>
                </div>
            </div>

            <div className='layout-card'>
                {items.length === 0 ?
                    (
                        <div className='py-16 text-center text-gray-500'>
                            該当する文言がありません
                        </div>
                    ) :
                    (
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-gray-200 text-left'>
                                        <th className='py-2 px-2'>階層</th>
                                        <th className='py-2 px-2'>モチーフ</th>
                                        <th className='py-2 px-2'>言語</th>
                                        <th className='py-2 px-2 w-[40%]'>本文</th>
                                        <th className='py-2 px-2'>採否</th>
                                        <th className='py-2 px-2 text-right'>使用</th>
                                        <th className='py-2 px-2'>生成元</th>
                                        <th className='py-2 px-2 w-[180px]'>採否変更</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(it => (
                                        <tr key={it.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                            <td className='py-2 px-2'>{FORTUNE_LABEL[it.fortune] ?? `#${it.fortune}`}</td>
                                            <td className='py-2 px-2'>{MOTIF_LABEL[it.motif] ?? it.motif}</td>
                                            <td className='py-2 px-2'>{LANG_LABEL[it.language] ?? it.language}</td>
                                            <td className='py-2 px-2'>
                                                <div className='whitespace-pre-wrap text-[13px]' style={{ maxHeight: '120px', overflow: 'auto' }}>
                                                    {it.body}
                                                </div>
                                            </td>
                                            <td className='py-2 px-2'>
                                                <span className={`inline-block px-2 py-0.5 rounded text-[12px] ${STATUS_VALUE_TO_COLOR[it.status] ?? 'bg-gray-100'}`}>
                                                    {STATUS_VALUE_TO_LABEL[it.status] ?? `#${it.status}`}
                                                </span>
                                            </td>
                                            <td className='py-2 px-2 text-right font-mono text-[12px]'>{it.use_count}</td>
                                            <td className='py-2 px-2 text-[12px] text-gray-500'>{it.generated_by}</td>
                                            <td className='py-2 px-2'>
                                                <div className='flex flex-wrap gap-1'>
                                                    <Button
                                                        size='sm'
                                                        variant={it.status === 1 ? 'active' : 'outline'}
                                                        onClick={() => handleStatusChange(it.id, 1)}
                                                        disabled={it.status === 1}
                                                    >
                                                        採用
                                                    </Button>
                                                    <Button
                                                        size='sm'
                                                        variant={it.status === 2 ? 'active' : 'outline'}
                                                        onClick={() => handleStatusChange(it.id, 2)}
                                                        disabled={it.status === 2}
                                                    >
                                                        却下
                                                    </Button>
                                                    <Button
                                                        size='sm'
                                                        variant={it.status === 3 ? 'active' : 'outline'}
                                                        onClick={() => handleStatusChange(it.id, 3)}
                                                        disabled={it.status === 3}
                                                    >
                                                        お気に入り
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </div>

            <GenerateSheet
                open={generateOpen}
                onOpenChange={setGenerateOpen}
                onGenerate={async (payload) => {
                    const res = await flowResult(generateTexts(payload));
                    if (res?.ok) {
                        toastify('success', `${payload.count} 件生成しました`);
                        setGenerateOpen(false);
                        reload();
                    } else {
                        toastify('error', res?.message || '生成に失敗しました');
                    }
                }}
            />
        </div>
    );
});

interface FilterSelectProps {
    label: string,
    value: string,
    onChange: (v: string) => void,
    options: { value: string, label: string }[]
}
const FilterSelect = ({ label, value, onChange, options }: FilterSelectProps) => (
    <div className='flex flex-col gap-1'>
        <label className='text-[12px] font-medium'>{label}</label>
        <Select onValueChange={onChange} value={value}>
            <SelectTrigger className='w-[160px]'>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {options.map(opt => (
                    <SelectItem key={opt.value || 'all'} value={opt.value} className='hover:cursor-pointer'>
                        {opt.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

interface GenerateSheetProps {
    open: boolean,
    onOpenChange: (v: boolean) => void,
    onGenerate: (payload: { fortune: number, motif: string, language: string, count: number }) => Promise<void>
}
const GenerateSheet = ({ open, onOpenChange, onGenerate }: GenerateSheetProps) => {
    const form = useForm({
        resolver: yupResolver(generateSchema),
        mode: 'onSubmit',
        defaultValues: {
            fortune: 1,
            motif: 'whaleshark',
            language: 'ja',
            count: 5
        } as any
    });

    useEffect(() => {
        if (open) {
            form.reset({
                fortune: 1,
                motif: 'whaleshark',
                language: 'ja',
                count: 5
            } as any);
        }
    }, [open]);

    const onSubmit = async (data: any) => {
        await onGenerate({
            fortune: Number(data.fortune),
            motif: String(data.motif),
            language: String(data.language),
            count: Number(data.count)
        });
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[400px] sm:w-[440px]' side='right'>
                <SheetHeader>
                    <SheetTitle>占い文言を追加生成</SheetTitle>
                    <SheetDescription>
                        Claude API で指定の階層 / モチーフ / 言語の組合せで N 件追加生成します。
                        モデルは設定画面の OMIKUJI_LLM_MODEL を使用。
                    </SheetDescription>
                </SheetHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='mt-4 space-y-4'>
                        <FormField
                            control={form.control}
                            name='fortune'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>階層 *</FormLabel>
                                    <FormControl>
                                        <Select
                                            onValueChange={v => field.onChange(Number(v))}
                                            value={String(field.value ?? 1)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {FORTUNE_OPTIONS.slice(1).map(o => (
                                                    <SelectItem key={o.value} value={o.value} className='hover:cursor-pointer'>
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='motif'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>モチーフ *</FormLabel>
                                    <FormControl>
                                        <Select
                                            onValueChange={field.onChange}
                                            value={typeof field.value === 'string' ? field.value : 'whaleshark'}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {MOTIF_OPTIONS.slice(1).map(o => (
                                                    <SelectItem key={o.value} value={o.value} className='hover:cursor-pointer'>
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='language'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>言語 *</FormLabel>
                                    <FormControl>
                                        <Select
                                            onValueChange={field.onChange}
                                            value={typeof field.value === 'string' ? field.value : 'ja'}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {LANGUAGE_OPTIONS.slice(1).map(o => (
                                                    <SelectItem key={o.value} value={o.value} className='hover:cursor-pointer'>
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='count'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>生成件数 (1〜50) *</FormLabel>
                                    <FormControl>
                                        <Input type='number' {...field} value={field.value ?? ''} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <SheetFooter className='pt-4'>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                キャンセル
                            </Button>
                            <Button type='submit' variant='active'>
                                生成
                            </Button>
                        </SheetFooter>
                    </form>
                </Form>
            </SheetContent>
        </Sheet>
    );
};
